import { IconButton } from "@/components/atoms/IconButton/IconButton";
import { IconButtonWithText } from "@/components/atoms/IconButtonWithText/IconButtonWithText";
import { FeatureGate } from "@/components/molecules/FeatureGate/FeatureGate";
import { OptionsMenu } from "@/components/molecules/OptionsMenu/OptionsMenu";
import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import { useDashboardListUI } from "@/dashboard/DashboardListUIProvider";
import { DashboardTagFilter } from "@/dashboard/DashboardTagFilter";
import { buildSortMenuItems } from "@/dashboard/utils";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import { pageProfiles, startsWithExtraPath } from "@/route";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import { FAB, Input } from "@mm/metx-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useHistory, useRouteMatch } from "react-router-dom";
import type { FilterAndSorterFormTypes } from "./types";

export type DashboardHeaderProps = {
  onSortChange?: (sort: FilterAndSorterFormTypes["sort"]) => void;
  showCollapseButton?: undefined | boolean;
  isSideNav?: boolean;
};

export function DashboardHeader({ onSortChange, showCollapseButton, isSideNav }: DashboardHeaderProps) {
  const desktop = useDesktop();
  const match = useRouteMatch();
  const history = useHistory();

  const { expandAll, toggleExpandAll } = useDashboardListUI();

  const { register, setValue, watch } = useFormContext<FilterAndSorterFormTypes>();
  const activeSort = watch("sort");

  // The page header buttons (account, import, notifications) overlay the top right of the viewport. Shortly before
  // this sticky header reaches them (and while it is pinned to the top) the sort menu moves next to the add button.
  const headerRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  useEffect(() => {
    if (isSideNav || !headerRef.current) {
      return;
    }
    // The negative root margin makes the ratio drop below 1 once the header's top is within that distance of the viewport top.
    const observer = new IntersectionObserver(([entry]) => setIsStuck(entry.intersectionRatio < 1), {
      rootMargin: "-72px 0px 0px 0px",
      threshold: [1],
    });
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [isSideNav]);

  const filterOptions = {
    title: t`Title`,
    time_created: t`Creation Date`,
    time_updated: t`Edit Date`,
  };

  const unfold = (
    <div className="dashboards__header-unfold">
      {showCollapseButton && (
        <TooltipWrapper
          tooltipText={expandAll ? t`Collapse all` : t`Expand all`}
          position="left"
          tail={true}
          delay={700}
        >
          <IconButton
            iconName={expandAll ? "unfold_less" : "unfold_more"}
            ariaLabel={expandAll ? t`Collapse all` : t`Expand all`}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpandAll();
            }}
          />
        </TooltipWrapper>
      )}
      <TooltipWrapper tooltipText={t`Sort menu`} position="left" tail={true} delay={700}>
        <OptionsMenu
          iconName="list"
          ariaLabel={t`Sort`}
          className={""}
          items={buildSortMenuItems(filterOptions, activeSort, setValue, onSortChange)}
        />
      </TooltipWrapper>
    </div>
  );

  return (
    <div ref={headerRef} className={classNames("dashboards__header", isStuck && "dashboards__header--stuck")}>
      {isSideNav && (
        <div className="dashboards__header-title">
          <TooltipWrapper tooltipText={t`View all dashboards`} position="right" tail={true} delay={700}>
            <IconButtonWithText
              iconName="home"
              label={t`Your Dashboards`}
              aria-label="Home"
              data-testid="dashboard-home-btn"
              onClick={() => {
                desktop.dispatch({ type: "setAllWindowVisibility", isVisible: false });
                history.push(`${startsWithExtraPath(match.path)}${pageProfiles.asString()}`);
              }}
            />
          </TooltipWrapper>
          {unfold}
        </div>
      )}
      <div className="dashboards__header-control">
        <Input
          iconLeading={<span className="material-icons">search</span>}
          htmlType="search"
          className="search-input"
          placeholder={t`Search by name`}
          fullWidth={isSideNav}
          {...register("query")}
        />
        <FeatureGate path={"ui.dashboard.add"}>
          <TooltipWrapper tooltipText={t`Create new Dashboard`} position="right" tail={true} delay={700}>
            <FAB
              shape="square"
              icon={
                <span className="material-icons" aria-hidden="true">
                  add
                </span>
              }
              aria-label={t`Create new Dashboard`}
              data-testid="add-dashboard-card"
              onClick={() =>
                desktop.dispatch({
                  type: "toggleWindowVisibility",
                  windowId: instanceId(WindowKind.AddDashboard),
                })
              }
            />
          </TooltipWrapper>
        </FeatureGate>
        {!isSideNav && unfold}
      </div>
      <FeatureGate path={"ui.dashboard.edit"}>
        <DashboardTagFilter isSideNav={isSideNav} />
      </FeatureGate>
    </div>
  );
}
